// Analytics-only Shopify cart.
//
// Shopify Analytics only counts an add-to-cart when the event points at a
// Shopify cart that was changed through the Storefront API with the
// visitor's tracking tokens. OpenStore storefronts keep their real cart in
// OpenStore, so we ask the backend to mirror each add into a Shopify cart
// (POST /api/v1/cart-additions) and send that cart's id with the event.
//
// The mirror cart is never shown to the shopper and never checked out. We
// keep its id in our own storage key — never Shopify's `cart` cookie — so
// no Shopify theme on the same domain can pick it up.

import { API_BASE_URL } from './bootstrap';
import type { TrackingTokens } from './tracking';

const FETCH_TIMEOUT_MS = 5_000;
const STORAGE_PREFIX = '__ratio_shopify_mirror_cart_';

export interface MirrorLine {
	variantId: string;
	quantity: number;
}

export interface MirrorResult {
	/** Shopify Cart GID to send as the event's cartId. */
	cartId: string;
	/** Product GID per variant GID, from the cart Shopify returned. */
	productIdByVariant: Map<string, string>;
}

function storageKey(shopId: string): string {
	return `${STORAGE_PREFIX}${shopId}`;
}

function readStoredCart(shopId: string): string {
	try {
		return localStorage.getItem(storageKey(shopId)) || '';
	} catch {
		return '';
	}
}

function storeCart(shopId: string, cartId: string): void {
	try {
		localStorage.setItem(storageKey(shopId), cartId);
	} catch {
		// localStorage unavailable — the next add starts a fresh cart.
	}
}

/**
 * Add `lines` to this visitor's mirror cart (creating it on first use).
 * Returns null on any failure — callers fall back to sending the event
 * without a Shopify cart.
 */
export async function mirrorAddToCart(
	shopId: string,
	tokens: TrackingTokens,
	lines: MirrorLine[],
): Promise<MirrorResult | null> {
	if (!lines.length) return null;

	const existing = readStoredCart(shopId);
	const body: Record<string, unknown> = {
		lines,
		uniqueToken: tokens.uniqueToken,
		visitToken: tokens.visitToken,
	};
	if (existing) body.cartId = existing;

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
	let res: Response;
	try {
		res = await fetch(`${API_BASE_URL}/api/v1/cart-additions`, {
			method: 'POST',
			credentials: 'omit',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
			signal: controller.signal,
		});
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[shopify-pixel] cart-additions request failed:', err);
		return null;
	} finally {
		clearTimeout(timer);
	}

	if (!res.ok) {
		// eslint-disable-next-line no-console
		console.warn(`[shopify-pixel] cart-additions ${res.status} ${res.statusText}`);
		return null;
	}

	const json = (await res.json()) as Record<string, unknown>;
	const data = (json.data ?? json) as {
		cartId?: unknown;
		products?: { variantId?: unknown; productId?: unknown }[];
	};
	if (typeof data.cartId !== 'string' || !/^gid:\/\/shopify\/Cart\//.test(data.cartId)) {
		// eslint-disable-next-line no-console
		console.warn('[shopify-pixel] cart-additions returned no cart');
		return null;
	}

	storeCart(shopId, data.cartId);

	const productIdByVariant = new Map<string, string>();
	for (const p of data.products ?? []) {
		if (typeof p.variantId === 'string' && typeof p.productId === 'string') {
			productIdByVariant.set(p.variantId, p.productId);
		}
	}
	return { cartId: data.cartId, productIdByVariant };
}
